import React, { useEffect, useRef, useState } from 'react';

import { createStyles, Fade, Paper, Popper, Theme, WithStyles, withStyles } from '@material-ui/core';
import classnames from 'classnames';
import { isString, isUndefined } from 'lodash';

import { SvgIconWrapper, Typography } from 'components/v2';

import AvatarLumaBot from '../../../components/AvatarLumaBot';
import useSettingsLumaBot from '../../../hooks/useSettingsLumaBot';

import Fab, { FabProps } from './Fab';
import IconCloseButton from './IconCloseButton';

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: 220,
      boxSizing: 'border-box',
      padding: theme.spacing.unit * 1.5,
      cursor: 'pointer',
      boxShadow: '0 4px 10px 0 rgba(128, 128, 128, 0.25)',
    },
    iconClosePrompt: {
      cursor: 'pointer',
      position: 'absolute',
      marginLeft: -44,
      top: -8,
    },
    popper: {
      '&::after': {
        content: '""',
        width: 15,
        height: 15,
        backgroundColor: 'white',
        boxShadow: '3px -3px 7px -4px rgba(128, 128, 128, 0.25)',
        display: 'inline-block',
        position: 'absolute',
        top: '50%',
        right: -5,
        transform: 'rotate(45deg)',
        animation: 'fadein 1s',
      },
    },
    text: {
      fontFamily: '"Open Sans", sans-serif',
      fontStyle: 'normal',
    },
    avatarFabIcon: {
      width: '100%',
      height: '100%',
    },
    readOnly: {
      pointerEvents: 'none',
    },
    hidden: {
      visibility: 'hidden',
    },
    '@keyframes fadein': {
      '0%': {
        opacity: 0,
      },
      '100%': {
        opacity: 1,
      },
    },
  });

export interface FabLumaBotProps extends Omit<FabProps, 'classes' | 'children' | 'onClick'>, WithStyles<typeof styles> {
  readOnly?: boolean;
  onToggle: () => void;
  onClosePrompt: () => void;
}

const FabLumaBot: React.FC<FabLumaBotProps> = ({ classes, readOnly, onToggle, onClosePrompt, ...rest }) => {
  const buttonRef = useRef<HTMLDivElement>(null);
  const promptOpenedRef = useRef(false);
  const { prompt, avatar } = useSettingsLumaBot();
  const [promptOpen, setPromptOpen] = useState(!!prompt);

  // Using an effect since settings are retrieved async
  useEffect(() => {
    if (
      // Skip if `prompt` is undefined (settings not available yet)
      !isString(prompt) ||
      // Additional flag to skip reopening the prompt when the iframe re-renders
      promptOpenedRef.current
    ) {
      return;
    }
    setPromptOpen(!!prompt);

    // Triggering the method so we can resize the iframe to exclude the prompt area
    if (!prompt) onClosePrompt();

    // Ensure we don't trigger this functionality again
    promptOpenedRef.current = true;
  }, [prompt, promptOpen, onClosePrompt]);

  const handleOpen = () => {
    setPromptOpen(false);
    onToggle();
  };

  return (
    <>
      <Popper
        className={classnames(classes.popper, { [classes.readOnly]: readOnly })}
        open={!!promptOpen}
        anchorEl={buttonRef.current}
        placement="left"
        modifiers={{ offset: { enabled: true, offset: '0,16' } }}
        transition
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={1000} exit={false}>
            <Paper className={classes.paper} onClick={handleOpen}>
              <SvgIconWrapper
                fontSize="large"
                className={classes.iconClosePrompt}
                Icon={IconCloseButton}
                color="supportSteel"
                onClick={(event) => {
                  event.stopPropagation();
                  setPromptOpen(false);
                  onClosePrompt();
                }}
              />
              <Typography variant="supportW400F14" className={classes.text}>
                {prompt}
              </Typography>
            </Paper>
          </Fade>
        )}
      </Popper>
      <Fab
        className={classnames({
          [classes.readOnly]: readOnly,
          [classes.hidden]: isUndefined(avatar),
        })}
        buttonRef={buttonRef}
        onClick={handleOpen}
        disableRipple
        {...rest}
      >
        <AvatarLumaBot className={classes.avatarFabIcon} src={avatar as string} />
      </Fab>
    </>
  );
};

export default withStyles(styles, { name: 'FabLumaBot' })(FabLumaBot);
